import { Link } from 'react-router-dom';
import { Plant } from '../types/plants';

interface PlantCardProps {
  plant: Plant;
  currentLetter?: string;
}

const PlantCard = ({ plant, currentLetter }: PlantCardProps) => {
  // Debug log to verify the currentLetter prop
  console.log(`PlantCard for ${plant.latin_name || plant.common_names} - currentLetter:`, currentLetter);
  
  console.log(`PlantCard - currentLetter:`, currentLetter);
  
  const linkTo = {
    pathname: `/botanicals/${plant._id}`,
    search: currentLetter && currentLetter !== 'All' ? `letter=${currentLetter}` : '',
    state: { 
      from: 'botanicals',
      search: currentLetter && currentLetter !== 'All' ? `?letter=${currentLetter}` : ''
    }
  };

  // Use imageUrl from the model
  const imageUrl = plant.imageUrl;
  
  // Transform Unsplash URL if needed
  const getImageUrl = (url: string | undefined) => {
    if (!url) return null;
    if (url.includes('unsplash.com/photos/')) {
      return url.replace('unsplash.com/photos/', 'images.unsplash.com/photo-');
    }
    return url;
  };
  
  const finalImageUrl = getImageUrl(imageUrl);
  
  console.log('PlantCard debug:', {
    plantName: plant.latin_name || (Array.isArray(plant.common_names) ? plant.common_names[0] : plant.common_names),
    hasImageUrl: !!plant.imageUrl,
    imageUrl: plant.imageUrl,
    finalImageUrl
  });
  
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow h-full flex flex-col">
      <Link 
        to={linkTo}
        className="block h-40 bg-gray-100 overflow-hidden relative"
      >
        {finalImageUrl ? (
          <div className="relative w-full h-full overflow-hidden">
            <img 
              src={finalImageUrl} 
              alt={plant.latin_name || (Array.isArray(plant.common_names) ? plant.common_names[0] : plant.common_names) || 'Plant image'}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              onLoad={(e) => {
                console.log('Image loaded successfully:', (e.target as HTMLImageElement).src);
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                console.error('Image failed to load:', target.src);
                
                // Hide the broken image
                target.style.display = 'none';
                
                // Show the placeholder
                const placeholder = target.parentElement?.querySelector('.placeholder') as HTMLElement;
                if (placeholder) {
                  placeholder.style.display = 'flex';
                }
              }}
            />
          </div>
      ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-400">
            <div className="text-4xl mb-2">🌿</div>
            <div>No image available</div>
          </div>
        )}
      </Link>
      
      <div className="p-5 flex-1 flex flex-col">
        {/* Plant Name and Family */}
        <div className="mb-3">
          <Link 
            to={{
              pathname: `/botanicals/${plant._id}`,
              search: currentLetter && currentLetter !== 'All' ? `?letter=${currentLetter}` : ''
            }} 
            className="hover:underline block"
          >
            <h3 
              className="text-lg font-semibold text-gray-800 mb-1" 
              title={plant.latin_name || (Array.isArray(plant.common_names) ? plant.common_names[0] : plant.common_names || '')}
            >
              {plant.latin_name || (Array.isArray(plant.common_names) ? plant.common_names[0] : plant.common_names || '')}
            </h3>
          </Link>
          {plant.latin_name && plant.common_names && (
            <p 
              className="text-sm text-gray-600 mb-1" 
              title={Array.isArray(plant.common_names) ? plant.common_names.join(', ') : plant.common_names}
            >
              {Array.isArray(plant.common_names) ? plant.common_names.join(', ') : plant.common_names}
            </p>
          )}
          {plant.family && (
            <p className="text-xs text-gray-500" title={`Family: ${plant.family}`}>
              {plant.family}
            </p>
          )}
        </div>
        
        {/* Ratings */}
        <div className="flex gap-4 mb-3">
          {plant.medicinal_rating !== undefined && (
            <div className="text-center">
              <div className="text-xs font-medium text-gray-700">Medicinal</div>
              <div className="text-green-600 text-base leading-tight">
                {'★'.repeat(Math.min(5, plant.medicinal_rating))}
                <span className="text-gray-200">
                  {'★'.repeat(Math.max(0, 5 - (plant.medicinal_rating || 0)))}
                </span>
              </div>
            </div>
          )}
          {plant.edibility_rating !== undefined && (
            <div className="text-center">
              <div className="text-xs font-medium text-gray-700">Edibility</div>
              <div className="text-blue-600 text-base leading-tight">
                {'★'.repeat(Math.min(5, plant.edibility_rating))}
                <span className="text-gray-200">
                  {'★'.repeat(Math.max(0, 5 - (plant.edibility_rating || 0)))}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Medicinal Properties - Only show 2 max */}
        {plant.medicinal_properties && plant.medicinal_properties.length > 0 && (
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {plant.medicinal_properties.slice(0, 2).map((property, index) => (
                <span 
                  key={index}
                  className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded"
                >
                  {property}
                </span>
              ))}
              {plant.medicinal_properties.length > 2 && (
                <span className="text-xs text-gray-500">+{plant.medicinal_properties.length - 2} more</span>
              )}
            </div>
          </div>
        )}

        {/* Bottom section with preparations and view button */}
        <div className="mt-auto pt-3 border-t border-gray-100">
          {/* Preparation Methods - Show up to 2 methods */}
          {plant.preparations && plant.preparations.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-medium text-gray-500 mb-1">Preparation Methods:</div>
              <div className="flex flex-wrap gap-1.5">
                {plant.preparations.slice(0, 2).map((prep, index) => {
                  // Handle both string and Preparation object types
                  const method = typeof prep === 'string' ? prep : prep.method || 'Unknown';
                  const partUsed = typeof prep === 'string' ? '' : prep.part_used || '';
                  
                  return (
                    <div 
                      key={index}
                      className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded border border-green-100"
                    >
                      <div className="font-medium">{method}</div>
                      {partUsed && (
                        <div className="text-green-600 text-xs">{partUsed}</div>
                      )}
                    </div>
                  );
                })}
                {plant.preparations.length > 2 && (
                  <div className="text-xs text-gray-500 self-center">
                    +{plant.preparations.length - 2} more
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="flex justify-end">
            <Link
              to={`/botanicals/${plant._id}`}
              className="text-blue-600 hover:text-blue-800 text-xs font-medium hover:underline"
            >
              View details →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlantCard;